export const EXPORT_ORDER_COLUMNS = [
    { id: 'materialDescription', defaultSelected: true },
    { id: 'customerOrderNumber', defaultSelected: true },
    { id: 'salesNumber', defaultSelected: true },
    { id: 'transporterName', defaultSelected: true },
    { id: 'shipmentDate', defaultSelected: true },
    { id: 'deliveryDate', defaultSelected: true },
    { id: 'shipmentQuantity', defaultSelected: true },
    { id: 'nfNumber', defaultSelected: true },
    { id: 'status', defaultSelected: true },
    { id: 'materialCode', defaultSelected: false },
    { id: 'portalOrderNumber', defaultSelected: false },
    { id: 'orderItem', defaultSelected: false },
    { id: 'incoterms', defaultSelected: false },
    { id: 'paymentCondition', defaultSelected: false },
    { id: 'totalValue', defaultSelected: false },
    { id: 'unitValue', defaultSelected: false },
    { id: 'discountValue', defaultSelected: false },
    { id: 'freightValue', defaultSelected: false },
    { id: 'subtotalValue', defaultSelected: false },
    { id: 'nfSeries', defaultSelected: false },
    { id: 'transporterCode', defaultSelected: false },
    { id: 'vehicleArrivalDate', defaultSelected: false },
    { id: 'vehicleEntryDate', defaultSelected: false },
    { id: 'loadDate', defaultSelected: false },
    { id: 'invoiceDate', defaultSelected: false },
    { id: 'departureDate', defaultSelected: false },
    { id: 'orderMaterial', defaultSelected: false },
    { id: 'observations', defaultSelected: false },
    { id: 'center', defaultSelected: false },
    { id: 'vehicle', defaultSelected: false },
    { id: 'receiver', defaultSelected: false },
] as const;

export type ExportColumnId = typeof EXPORT_ORDER_COLUMNS[number]['id'];

export const ALL_EXPORT_COLUMN_IDS: ExportColumnId[] = EXPORT_ORDER_COLUMNS.map(column => column.id);

export const DEFAULT_EXPORT_COLUMN_IDS: ExportColumnId[] = EXPORT_ORDER_COLUMNS
    .filter(column => column.defaultSelected)
    .map(column => column.id);

export const EXPORT_VIEW_CODE_TO_COLUMN_ID: Record<number, ExportColumnId> = {
    3: 'materialDescription',
    4: 'shipmentQuantity',
    5: 'shipmentDate',
    6: 'vehicle',
    8: 'transporterName',
    9: 'transporterCode',
    11: 'customerOrderNumber',
    12: 'orderMaterial',
    13: 'orderItem',
    14: 'receiver',
    16: 'portalOrderNumber',
    17: 'salesNumber',
    19: 'incoterms',
    20: 'paymentCondition',
    21: 'totalValue',
    22: 'nfNumber',
    24: 'status',
    25: 'vehicleArrivalDate',
    26: 'vehicleEntryDate',
    27: 'loadDate',
    28: 'invoiceDate',
    31: 'deliveryDate',
};

export const isExportColumnId = (value: string): value is ExportColumnId => {
    return ALL_EXPORT_COLUMN_IDS.includes(value as ExportColumnId);
};

export const sanitizeExportColumnIds = (columns?: string[]): ExportColumnId[] => {
    if (!columns?.length) {
        return [...DEFAULT_EXPORT_COLUMN_IDS];
    }

    const unique = columns.filter((column, index) => isExportColumnId(column) && columns.indexOf(column) === index) as ExportColumnId[];
    return unique.length ? unique : [...DEFAULT_EXPORT_COLUMN_IDS];
};

export const sanitizeColumnOrder = (columns?: string[]): ExportColumnId[] => {
    const unique = (columns || []).filter((column, index, list) => isExportColumnId(column) && list.indexOf(column) === index) as ExportColumnId[];
    const missing = ALL_EXPORT_COLUMN_IDS.filter(id => !unique.includes(id));
    return unique.length ? [...unique, ...missing] : [...ALL_EXPORT_COLUMN_IDS];
};

export const uniqueCatalogIds = (ids: string[] | undefined, catalogIds: string[]): string[] => {
    return (ids || []).filter((id, index, list) => catalogIds.includes(id) && list.indexOf(id) === index);
};

export const sanitizeGuidColumnOrder = (columns: string[] | undefined, catalogIds: string[]): string[] => {
    const unique = uniqueCatalogIds(columns, catalogIds);
    const missing = catalogIds.filter(id => !unique.includes(id));
    return unique.length ? [...unique, ...missing] : [...catalogIds];
};

export const applyFavoriteColumnOrder = (favoriteColumnIds: string[], catalogIds: string[]): string[] => {
    const selected = uniqueCatalogIds(favoriteColumnIds, catalogIds);
    const remaining = catalogIds.filter(id => !selected.includes(id));
    return [...selected, ...remaining];
};

export const prioritizeSelectedColumnOrder = (columnOrder: string[], selectedIds: string[]): string[] => {
    const selected = columnOrder.filter(id => selectedIds.includes(id));
    const unselected = columnOrder.filter(id => !selectedIds.includes(id));
    return [...selected, ...unselected];
};

export const getDefaultMappedColumnIds = (columns: { id: string; code: number }[]): string[] => {
    return columns
        .filter(column => {
            const mapped = EXPORT_VIEW_CODE_TO_COLUMN_ID[column.code];
            return mapped ? DEFAULT_EXPORT_COLUMN_IDS.includes(mapped) : false;
        })
        .map(column => column.id);
};

export const mapExportViewColumnsToExcelIds = (columns: { code: number }[]): ExportColumnId[] => {
    const ids: ExportColumnId[] = [];
    columns.forEach(column => {
        const mapped = EXPORT_VIEW_CODE_TO_COLUMN_ID[column.code];
        if (mapped && !ids.includes(mapped)) {
            ids.push(mapped);
        }
    });
    return ids;
};

export const moveColumnInOrder = <T extends string>(columns: T[], fromId: T, toId: T): T[] => {
    if (fromId === toId) {
        return columns;
    }

    const from = columns.indexOf(fromId);
    const to = columns.indexOf(toId);
    if (from < 0 || to < 0) {
        return columns;
    }

    const next = columns.filter(id => id !== fromId);
    let insertAt = next.indexOf(toId);
    if (from < to) {
        insertAt += 1;
    }
    next.splice(insertAt, 0, fromId);
    return next;
};
